"use client"

import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import Image from "next/image"

import { cn } from "@/lib/utils"

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    )}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, alt = "", src, ...props }, ref) => {
  // Sem src: nem renderiza — o Radix mantém o fallback visível (comportamento
  // idêntico ao anterior, em que src undefined levava ao estado de erro).
  if (!src) return null
  const isLocal = typeof src === "string" && src.startsWith("/")
  return (
    <AvatarPrimitive.Image
      ref={ref}
      // O Radix decide renderizar (ou não) a imagem pela sondagem DELE, que usa
      // este src — sem repassá-lo aqui o status fica eternamente diferente de
      // "loaded" e o componente retorna null (só o fallback aparecia).
      src={typeof src === "string" ? src : undefined}
      {...props}
      asChild
    >
      <Image
        src={src}
        alt={alt}
        width={48}
        height={48}
        className={cn("aspect-square h-full w-full", className)}
        // Uploads locais (/uploads/*) são servidos pelo Caddy fora do Next —
        // o otimizador não alcança esses arquivos; só avatares remotos passam
        // pela otimização (item 4.3a do PLANO_FOCADO_PEDILLO.md).
        unoptimized={isLocal}
      />
    </AvatarPrimitive.Image>
  )
})
AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    )}
    {...props}
  />
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }
